import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, CircularProgress, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useStorefront } from '../../components/Storefront/StorefrontContext';
import { ProductPrice } from '../../components/Storefront/StorefrontProductCard';
import { formatMoney } from '../../components/Storefront/storefrontHelpers';
import { useStorefrontT } from '../../components/Storefront/useStorefrontT';
import MaterialSymbol from '../../components/common/MaterialSymbol';
import { useStorefrontProducts } from './useStorefrontProducts';

/** `/store/:slug/cart` — Warenkorb (nur im Browser; der Server prüft beim Checkout erneut). */
const StorefrontCart: React.FC = () => {
    const { t, locale } = useStorefrontT();
    const { slug, cart, removeFromCart } = useStorefront();
    const { products } = useStorefrontProducts(slug);

    const items = useMemo(() => (products ?? []).filter((p) => cart.includes(p.id)), [products, cart]);
    const total = items.reduce((sum, p) => sum + Number(p.price?.amount ?? 0), 0);
    const currency = items[0]?.price?.currency ?? 'EUR';

    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom>{t('cart.title')}</Typography>
            {!products && cart.length > 0 ? <CircularProgress /> : null}
            {cart.length === 0 || (products && items.length === 0) ? <Typography color="text.secondary">{t('cart.empty')}</Typography> : null}
            <Stack divider={<Divider />} spacing={2} sx={{ my: 2 }}>
                {items.map((p) => (
                    <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>{p.title}</Typography>
                        <ProductPrice product={p} />
                        <IconButton aria-label={`${t('cart.remove')}: ${p.title}`} onClick={() => removeFromCart(p.id)}><MaterialSymbol name="delete" /></IconButton>
                    </Box>
                ))}
            </Stack>
            {items.length > 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6">{t('cart.total')}: {total > 0 ? formatMoney(total, currency, locale === 'en' ? 'en-GB' : 'de-DE') : t('product.free')} {total > 0 ? <Typography component="span" variant="caption" color="text.secondary">{t('product.vatIncluded')}</Typography> : null}</Typography>
                    <Button variant="contained" component={RouterLink} to={`/store/${slug}/checkout`}>{t('cart.toCheckout')}</Button>
                </Box>
            ) : null}
        </Box>
    );
};

export default StorefrontCart;
